import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { servicesAPI, publicAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';

const BACKEND_URL = '';

import { 
  ArrowRight, Search, Filter, RefreshCw, X, ArrowLeft, Menu,
  TrendingUp, Globe, Zap, Mail, Server, ShoppingCart, Inbox,
  Shield, Headphones, Star, CheckCircle, BarChart, PieChart,
  Monitor, Smartphone, Tablet, Laptop, Cpu, Database, Cloud,
  Code, Terminal, Settings, Wifi, Lock, Key,
  Image, Video, Music, Mic, Camera, MessageSquare, Phone,
  Send, Bell, Megaphone, Radio, Tv,
  File, FileText, Folder, Archive, Clipboard, BookOpen, Newspaper,
  CreditCard, DollarSign, Package, Truck, Tag, Receipt, Wallet,
  Users, User, Heart, ThumbsUp, Share, Link as LinkIcon, Globe2,
  Wrench, Hammer, Paintbrush, Scissors, Layers, Grid, Layout,
  Award, Target, Flag, Bookmark, Calendar, Clock, MapPin, Eye, Edit, Plus
} from 'lucide-react';

// Extended Icon mapping - matches IconSelector options
const iconMap = {
  TrendingUp, Globe, Zap, Mail, Server, ShoppingCart, Inbox,
  Shield, Headphones, Star, CheckCircle, BarChart, PieChart,
  Monitor, Smartphone, Tablet, Laptop, Cpu, Database, Cloud,
  Code, Terminal, Settings, Wifi, Lock, Key, RefreshCw,
  Image, Video, Music, Mic, Camera, MessageSquare, Phone,
  Send, Bell, Megaphone, Radio, Tv,
  File, FileText, Folder, Archive, Clipboard, BookOpen, Newspaper,
  CreditCard, DollarSign, Package, Truck, Tag, Receipt, Wallet,
  Users, User, Heart, ThumbsUp, Share, Globe2,
  Wrench, Hammer, Paintbrush, Scissors, Layers, Grid, Layout,
  Award, Target, Flag, Bookmark, Calendar, Clock, MapPin, Eye, Edit, Plus
};

const getIcon = (iconName) => iconMap[iconName] || Server;

// Logo helpers
const getLogoUrl = (settings) => {
  if (!settings?.logo_url) return null;
  if (settings.logo_url.startsWith('http')) return settings.logo_url;
  return `${BACKEND_URL}${settings.logo_url}`;
};

const getLogoInitials = (settings) => {
  const name = settings?.site_name || 'Edrano';
  return name.substring(0, 2).toUpperCase();
};

// Category labels
const categoryLabels = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  wordpress_support: 'WordPress Support',
  wordpress_upgrades: 'WordPress Upgrades',
  woocommerce_upgrades: 'WooCommerce',
  newsletter: 'Newsletter',
  accessibility: 'Accessibility',
  hosting: 'Hosting',
  email_client: 'Email Services',
};

// Category icons
const categoryIcons = {
  google_ads: 'TrendingUp',
  meta_ads: 'Globe',
  wordpress_support: 'Headphones',
  wordpress_upgrades: 'Zap',
  woocommerce_upgrades: 'ShoppingCart',
  newsletter: 'Mail',
  accessibility: 'Shield',
  hosting: 'Server',
  email_client: 'Inbox',
};

export default function ServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Redirect logged-in users to the authenticated services page
  useEffect(() => {
    if (user) {
      navigate('/dashboard/services', { replace: true });
    }
  }, [user, navigate]);

  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filters
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Get initial category from URL
  const urlCategory = searchParams.get('category');

  useEffect(() => {
    if (urlCategory) {
      setSelectedCategories([urlCategory]);
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, settingsRes] = await Promise.all([
        servicesAPI.getAll(),
        siteAPI.getSiteSettings()
      ]);
      setServices(servicesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const getServiceSlug = (service) => {
    return service.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
  };

  const getLowestPrice = (service) => {
    if (!service.pricing_tiers?.length) return null;
    return Math.min(...service.pricing_tiers.map(t => t.price));
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      const newCategories = prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category];
      
      if (newCategories.length === 1) {
        setSearchParams({ category: newCategories[0] });
      } else if (newCategories.length === 0) {
        setSearchParams({});
      } else {
        setSearchParams({});
      }
      
      return newCategories;
    });
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setPriceRange('all');
    setSearchTerm('');
    setSearchParams({});
  };

  // Filter and sort services
  const filteredServices = services.filter(service => {
    if (selectedCategories.length > 0 && !selectedCategories.includes(service.category)) {
      return false;
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!service.name.toLowerCase().includes(search) && 
          !service.short_description?.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    const lowestPrice = getLowestPrice(service);
    if (priceRange !== 'all' && lowestPrice) {
      if (priceRange === 'under100' && lowestPrice >= 100) return false;
      if (priceRange === '100-500' && (lowestPrice < 100 || lowestPrice > 500)) return false;
      if (priceRange === '500-1000' && (lowestPrice < 500 || lowestPrice > 1000)) return false;
      if (priceRange === 'over1000' && lowestPrice < 1000) return false;
    }
    
    return true;
  }).sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'price-low') return (getLowestPrice(a) || 0) - (getLowestPrice(b) || 0);
    if (sortBy === 'price-high') return (getLowestPrice(b) || 0) - (getLowestPrice(a) || 0);
    return 0;
  });

  // Get unique categories from services
  const availableCategories = [...new Set(services.map(s => s.category))];
  const hasActiveFilters = selectedCategories.length > 0 || priceRange !== 'all' || searchTerm;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Categories</Label>
        <div className="space-y-2">
          {availableCategories.map(cat => {
            const Icon = getIcon(categoryIcons[cat]);
            return (
              <div 
                key={cat} 
                className={`flex items-center space-x-3 p-2.5 rounded-xl cursor-pointer transition-colors ${
                  selectedCategories.includes(cat) 
                    ? 'bg-indigo-50 border border-indigo-200' 
                    : 'hover:bg-slate-50 border border-transparent'
                }`}
                onClick={() => toggleCategory(cat)}
              >
                <Checkbox
                  id={`cat-${cat}`}
                  checked={selectedCategories.includes(cat)}
                  onCheckedChange={() => toggleCategory(cat)}
                  className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                  data-testid={`filter-category-${cat}`}
                />
                <Icon className="h-4 w-4 text-slate-500" />
                <label htmlFor={`cat-${cat}`} className="text-sm text-slate-700 cursor-pointer flex-1">
                  {categoryLabels[cat] || cat}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Price Range</Label>
        <Select value={priceRange} onValueChange={setPriceRange}>
          <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200" data-testid="filter-price-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">All Prices</SelectItem>
            <SelectItem value="under100">Under €100</SelectItem>
            <SelectItem value="100-500">€100 - €500</SelectItem>
            <SelectItem value="500-1000">€500 - €1,000</SelectItem>
            <SelectItem value="over1000">Over €1,000</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={clearFilters} 
          className="w-full rounded-xl border-slate-200 hover:bg-slate-50"
          data-testid="clear-filters-btn"
        >
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="services-page">
      <div className="container-main mx-auto">
        {/* Header */}
        <header className="bg-slate-900 rounded-2xl mb-3 md:mb-4 sticky top-3 md:top-4 z-50 shadow-xl shadow-slate-900/20">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30 overflow-hidden">
                  {settings?.logo_url ? (
                    <img 
                      src={getLogoUrl(settings)} 
                      alt={settings?.site_name || 'Logo'}
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <span className="text-white font-bold text-lg">{getLogoInitials(settings)}</span>
                  )}
                </div>
                <span className="font-heading font-bold text-xl text-white">{settings?.site_name || 'Edrano'}</span>
              </Link>
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-slate-300 hover:text-white hover:bg-white/10 rounded-full"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm" 
                    className="btn-gradient text-white rounded-full px-5 shadow-lg shadow-indigo-500/30"
                    data-testid="register-btn"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="bg-white rounded-3xl p-4 sm:p-6 lg:p-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-600 transition-colors" data-testid="back-home-link">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-2">
              {selectedCategories.length === 1 
                ? categoryLabels[selectedCategories[0]] || 'Services'
                : 'All Services'
              }
            </h1>
            <p className="text-slate-600 text-lg">
              {selectedCategories.length === 1 
                ? `Browse our ${categoryLabels[selectedCategories[0]]?.toLowerCase() || ''} solutions`
                : 'Explore our full range of professional WordPress services'
              }
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex gap-3">
              {/* Mobile Filter Button */}
              <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="lg:hidden h-12 px-4 rounded-xl border-slate-200"
                    data-testid="mobile-filter-btn"
                  >
                    <Filter className="h-5 w-5 mr-2" />
                    Filters
                    {hasActiveFilters && (
                      <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-indigo-600 text-white">
                        {selectedCategories.length + (priceRange !== 'all' ? 1 : 0)}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="font-heading">Filters</SheetTitle>
                    <SheetDescription>Refine your search results</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] h-12 rounded-xl bg-white border-slate-200" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-8">
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="bg-slate-50 rounded-2xl p-6 sticky top-24">
                <h3 className="font-heading font-semibold text-slate-900 mb-6 flex items-center">
                  <Filter className="h-5 w-5 mr-2 text-indigo-600" />
                  Filters
                </h3>
                <FilterContent />
              </div>
            </aside>

            {/* Services Grid */}
            <main className="flex-1">
              {filteredServices.length === 0 ? (
                <div className="text-center py-20 bg-slate-50 rounded-2xl">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">No services found</h3>
                  <p className="text-slate-500 mb-6">Try adjusting your filters or search term</p>
                  <Button 
                    variant="outline" 
                    onClick={clearFilters}
                    className="rounded-full"
                    data-testid="clear-filters-empty-btn"
                  >
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-slate-500">
                      Showing <span className="font-semibold text-slate-900">{filteredServices.length}</span> service{filteredServices.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  
                  {/* Responsive Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                    {filteredServices.map((service) => {
                      const Icon = getIcon(service.icon || categoryIcons[service.category]);
                      const lowestPrice = getLowestPrice(service);
                      const slug = getServiceSlug(service);
                      const hasImage = service.image_url && service.image_url.length > 0;
                      const imageUrl = hasImage 
                        ? (service.image_url.startsWith('http') ? service.image_url : `${BACKEND_URL}${service.image_url}`)
                        : null;
                      
                      return (
                        <Link 
                          key={service.id} 
                          to={`/service/${slug}`}
                          className="group"
                          data-testid={`service-card-${service.id}`}
                        >
                          <Card className="h-full bg-slate-50 border-slate-100 rounded-2xl overflow-hidden card-hover">
                            <CardContent className="p-5">
                              {/* Service Icon or Image */}
                              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-violet-500/10 flex items-center justify-center mb-4 group-hover:from-indigo-500/20 group-hover:to-violet-500/20 transition-colors overflow-hidden">
                                {hasImage ? (
                                  <img 
                                    src={imageUrl} 
                                    alt={service.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`w-full h-full items-center justify-center ${hasImage ? 'hidden' : 'flex'}`}>
                                  <Icon className="h-7 w-7 text-indigo-600" />
                                </div>
                              </div>
                              
                              {/* Service Name */}
                              <h3 className="font-heading font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2 line-clamp-2">
                                {service.name}
                              </h3>
                              
                              {/* Category Badge */}
                              <Badge className="bg-white text-slate-600 hover:bg-white border border-slate-200 font-medium text-xs mb-3">
                                {categoryLabels[service.category] || service.category}
                              </Badge>
                              
                              {/* Short Description */}
                              {service.short_description && (
                                <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                                  {service.short_description}
                                </p>
                              )}
                              
                              {/* Price */}
                              {settings?.show_prices_to_visitors !== false && lowestPrice && (
                                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                                  <span className="text-sm text-slate-500">From</span>
                                  <span className="text-xl font-bold text-indigo-600">
                                    €{lowestPrice.toFixed(0)}
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
